/**
 * bitzSonic — BITZ Club International sonic identity helpers.
 *
 * Two zero-file Web Audio API generators that share the same harmonic family
 * (F major / Fmaj9) as the homepage ambient pad, so the brand sounds
 * recognisably "BITZ" across the website, dashboards and future surfaces.
 *
 *   playSignatureCue()  →  ~1.4s premium arrival tone (login / first dashboard).
 *   playShimmerCue()    →  ~1.2s breath of brightness for slide transitions.
 *
 * Design rules:
 *   - Single-shot, never repeating. No interval timers.
 *   - Created on demand, closed on completion (no lingering AudioContext).
 *   - Respects `localStorage.bitz-ambient-enabled === '0'` (explicit mute).
 *   - Respects `sessionStorage.bitz-signature-played` (once-per-session arrival).
 *   - Honors `prefers-reduced-motion` as a proxy for "calm browsing" — skips.
 */

const AMBIENT_PREF_KEY = 'bitz-ambient-enabled';
const SIGNATURE_SESSION_KEY = 'bitz-signature-played';

const isMuted = () => {
  try {
    return localStorage.getItem(AMBIENT_PREF_KEY) === '0';
  } catch (_) {
    return false;
  }
};

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
};

const getAudioCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  return new AC();
};

/**
 * Bell-like voice: fundamental sine + soft octave overtone, ADSR envelope.
 * Used for both signature and shimmer cues.
 */
const scheduleBellTone = (ctx, dest, freq, startAt, opts = {}) => {
  const {
    attack = 0.03,
    holdMs = 200,
    releaseMs = 1100,
    peakGain = 0.18,
    overtoneGain = 0.18,
    detuneCents = 0,
  } = opts;

  const voice = ctx.createGain();
  voice.gain.setValueAtTime(0, startAt);
  voice.gain.linearRampToValueAtTime(peakGain, startAt + attack);
  voice.gain.setValueAtTime(peakGain, startAt + attack + holdMs / 1000);
  voice.gain.exponentialRampToValueAtTime(
    0.0001,
    startAt + attack + holdMs / 1000 + releaseMs / 1000
  );
  voice.connect(dest);

  // Fundamental
  const f1 = ctx.createOscillator();
  f1.type = 'sine';
  f1.frequency.value = freq;
  f1.detune.value = detuneCents;
  f1.connect(voice);
  f1.start(startAt);
  f1.stop(startAt + attack + holdMs / 1000 + releaseMs / 1000 + 0.05);

  // Soft octave overtone for "bell" shimmer
  const overtoneGainNode = ctx.createGain();
  overtoneGainNode.gain.value = overtoneGain;
  const f2 = ctx.createOscillator();
  f2.type = 'sine';
  f2.frequency.value = freq * 2;
  f2.detune.value = detuneCents + 4;
  f2.connect(overtoneGainNode).connect(voice);
  f2.start(startAt);
  f2.stop(startAt + attack + holdMs / 1000 + releaseMs / 1000 + 0.05);
};

/**
 * Play the BITZ signature arrival cue.
 *
 * @param {Object} options
 * @param {boolean} options.oncePerSession  default true — skip if already played this session
 * @param {boolean} options.forceMuted      default false — bypass mute pref (for live preview only)
 */
export const playSignatureCue = (options = {}) => {
  const { oncePerSession = true, forceMuted = false } = options;
  try {
    if (!forceMuted && isMuted()) return;
    if (prefersReducedMotion()) return;
    if (oncePerSession) {
      const played = sessionStorage.getItem(SIGNATURE_SESSION_KEY);
      if (played) return;
    }
    const ctx = getAudioCtx();
    if (!ctx) return;

    // Master chain — gentle lowpass, soft master gain
    const master = ctx.createGain();
    master.gain.value = 0.85; // tone-level peak still passes through ADSR
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3800;
    filter.Q.value = 0.6;
    filter.connect(master);
    master.connect(ctx.destination);

    const now = ctx.currentTime + 0.04;
    // F major triad ascending — F5, A5, C6 — 80ms apart, with a long F4 warmth note.
    // Volumes intentionally low ("private member greeting" feel, not a notification).
    scheduleBellTone(ctx, filter, 698.46, now, {
      attack: 0.025,
      holdMs: 160,
      releaseMs: 1200,
      peakGain: 0.095,
    }); // F5
    scheduleBellTone(ctx, filter, 880.0, now + 0.085, {
      attack: 0.025,
      holdMs: 160,
      releaseMs: 1200,
      peakGain: 0.075,
    }); // A5
    scheduleBellTone(ctx, filter, 1046.5, now + 0.17, {
      attack: 0.025,
      holdMs: 200,
      releaseMs: 1300,
      peakGain: 0.062,
    }); // C6
    // Sub warmth — slow swell, gives the cue body without being loud
    scheduleBellTone(ctx, filter, 174.61, now, {
      attack: 0.3,
      holdMs: 380,
      releaseMs: 1000,
      peakGain: 0.035,
      overtoneGain: 0,
    }); // F3

    if (oncePerSession) {
      try {
        sessionStorage.setItem(SIGNATURE_SESSION_KEY, '1');
      } catch (_) {
        /* ignore */
      }
    }

    // Auto-close context shortly after tone ends to free resources
    setTimeout(() => {
      try {
        if (ctx.close) ctx.close();
      } catch (_) {
        /* ignore */
      }
    }, 1800);
  } catch (_) {
    /* fail silently — audio is decoration, never a blocker */
  }
};

/**
 * Play a brief "harmonic shimmer" — used on hero slide transitions.
 * Lighter than the signature cue: just a single high octave bell that swells
 * up and decays. Tied to the same Fmaj9 family.
 */
export const playShimmerCue = () => {
  try {
    if (isMuted()) return;
    if (prefersReducedMotion()) return;
    const ctx = getAudioCtx();
    if (!ctx) return;
    const master = ctx.createGain();
    master.gain.value = 0.6;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 4500;
    filter.connect(master);
    master.connect(ctx.destination);

    const now = ctx.currentTime + 0.02;
    // Brief A5 → C6 grace — half-step worth of upward motion.
    // Kept extremely quiet so it reads as "atmosphere change", not "sound".
    scheduleBellTone(ctx, filter, 880.0, now, {
      attack: 0.06,
      holdMs: 80,
      releaseMs: 750,
      peakGain: 0.038,
      overtoneGain: 0.06,
    });
    scheduleBellTone(ctx, filter, 1046.5, now + 0.2, {
      attack: 0.05,
      holdMs: 80,
      releaseMs: 850,
      peakGain: 0.032,
      overtoneGain: 0.05,
    });

    setTimeout(() => {
      try {
        if (ctx.close) ctx.close();
      } catch (_) {
        /* ignore */
      }
    }, 1300);
  } catch (_) {
    /* ignore */
  }
};

export const BITZ_SONIC_KEYS = {
  AMBIENT_PREF_KEY,
  SIGNATURE_SESSION_KEY,
};
